import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Commands, Search } from '@local/client-contracts';
import { PopupRef, UiIconModel } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuComponent, ContextMenuData, ContextMenuItem, ContextMenuService } from '@shared/components';
import { EventInfo, EventsService } from '@shared/services';
import { FlagsService } from '@shared/services/flags.service';
import { KeyboardService } from '@shared/services/keyboard.service';
import { ResourcesService } from '@shared/services/resources.service';
import { getModifiers, isEnterKey, KeyName } from '@local/ts-infra';
import { Logger } from '@unleash-tech/js-logger';
import { Tooltip } from 'primeng/tooltip';
import { take } from 'rxjs';
import { ComponentFocused } from 'src/app/bar/models/component-focused.model';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { ResultCommandService } from 'src/app/bar/services/commands/result-command.service';
import { ContextMenuInvokeSource } from 'src/app/bar/services/commands/results-item-context-menu-helper';
import { MethodsList } from 'src/app/bar/services/home-page.service';
import { HubService } from 'src/app/bar/services/hub.service';
import {
  actionToIcon,
  CommandContext,
  DisplayItemData,
  isEnabledSummary,
  ResultContextMenuItem,
  TelemetryTrigger,
} from 'src/app/bar/views/results';
import {
  MailBuildResultViewService,
  MailViewModel,
  Profile,
  Recipient,
} from 'src/app/bar/views/results-views/mail/mail-build-result-view.service';
import { MailCommandBuilderService } from 'src/app/bar/views/results-views/mail/mail-command-builder.service';
import { MailContextMenuHelper } from 'src/app/bar/views/results-views/mail/mail-context-menu-helper';
import { Action } from 'src/app/bar/views/results/models/view-filters';
import { GlobalErrorHandler } from 'src/app/global-error-handler';
import { MailService } from '../../services/mail.service';
import { WorkspacesService } from 'src/app/bar/services';
import { Constants } from '@local/common';

export type ContactLinkViewModel = { icon: UiIconModel; url: Commands.Command; type: string; context: CommandContext };

@UntilDestroy()
@Component({
  selector: 'mail-preview-header',
  templateUrl: './mail-preview-header.component.html',
  styleUrls: ['./mail-preview-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MailPreviewHeaderComponent implements OnDestroy, AfterViewInit {
  private activeContextMenu: PopupRef<ContextMenuComponent, ContextMenuData>;

  //keyboard
  private readonly COMP_INDEX = 1;
  private readonly defaultCountChars = 35;
  private keyHandlerId: string;
  private workspacesService: WorkspacesService = inject(WorkspacesService);
  markedIndex: number;
  methodsList: MethodsList = [];

  private logger: Logger;
  private _viewModel: MailViewModel;
  private _model: Search.ResultResourceItem & DisplayItemData;
  contextMenuHelper: MailContextMenuHelper;
  profile: Profile;
  countViewRecipients: number;
  indicatorIcon: UiIconModel = { type: 'font' };
  DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG = Constants.DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG;

  readonly INDICATE_SUMMARY_ICON: UiIconModel = {
    type: 'font',
    value: 'icon-board-list',
  };

  @Input() set model(val: Search.ResultResourceItem & DisplayItemData) {
    if (!val) return;
    this._model = val;
    this.cdr.markForCheck();
  }
  @Input() telemetrySearch: Partial<EventInfo>;
  @Input() set viewModel(value: MailViewModel) {
    if (!value) return;
    this._viewModel = value;
    this.profile = this.mailBuildResultViewService.buildProfile(this.viewModel);
    this.setViewRecipients();
    this.unregisterKeyHandler();

    this.indicatorIcon.value = null;
    if (this.viewModel?.hasCalendarAttachment) this.indicatorIcon.value = 'icon-calendar-empty';
    if (this.viewModel?.hasAttachment) this.indicatorIcon.value = 'icon-paperclip-side';

    this.cdr.markForCheck();
  }

  @Input() set componentFocused(val: ComponentFocused) {
    if (val?.compIndex === this.COMP_INDEX) {
      if (this.keyHandlerId) return;
      this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => {
        this.handleKeys(keys, event);
      });
      this.markedIndex = val?.direction === 'first' ? 0 : this.methodsList.length - 1;
      this.setMethodsList();
      return;
    }
    this.unregisterKeyHandler();
  }

  @Output() closePopup: EventEmitter<void> = new EventEmitter();
  @Output() componentFocusedExit: EventEmitter<string> = new EventEmitter<string>();
  @ViewChild('subtitle', { read: ElementRef }) subtitleRef: ElementRef<HTMLElement>;
  @ViewChild('title', { read: ElementRef }) titleRef: ElementRef<HTMLElement>;
  @ViewChild('actions', { read: ElementRef }) actionsRef: ElementRef;
  @ViewChild('openIcon', { read: ElementRef }) openRef: ElementRef;
  @ViewChild('icon', { read: ElementRef }) iconRef: ElementRef;
  @ViewChildren(Tooltip) private tooltips: QueryList<Tooltip>;

  get viewModel(): MailViewModel {
    return this._viewModel;
  }

  get model(): Search.ResultResourceItem & DisplayItemData {
    return this._model;
  }

  private get searchId() {
    return this.telemetrySearch?.search?.searchId;
  }

  private get eventResources() {
    return [
      {
        id: this.model?.id,
        list: this.hubService.currentLocation,
        appId: this.model?.link?.appId,
        type: this.model?.resource?.type,
        linkId: this.model?.link?.id,
      },
    ];
  }

  constructor(
    private builder: MailCommandBuilderService,
    private contextMenuService: ContextMenuService,
    private hostRef: ElementRef,
    private errorHandler: GlobalErrorHandler,
    private resourcesService: ResourcesService,
    private flagsService: FlagsService,
    private cdr: ChangeDetectorRef,
    private mailBuildResultViewService: MailBuildResultViewService,
    private mailService: MailService,
    private keyboardService: KeyboardService,
    private eventService: EventsService,
    private hubService: HubService,
    private commandService: CommandsService,
    private resultCommandService: ResultCommandService
  ) {}

  ngOnDestroy(): void {
    this.unregisterKeyHandler();
    if (this.contextMenuHelper.ref) this.contextMenuHelper.ref.destroy();
  }

  ngAfterViewInit(): void {
    this.contextMenuHelper = new MailContextMenuHelper(
      this.builder,
      this.logger,
      this.contextMenuService,
      this.hostRef,
      this.titleRef,
      this.subtitleRef,
      this.actionsRef,
      this.errorHandler,
      this.resourcesService,
      this.hubService,
      this.flagsService,
      this.workspacesService
    );
    this.contextMenuHelper.invoke$
      .pipe(untilDestroyed(this))
      .subscribe(async ({ item, trigger }) => await this.emitContextMenuCommand(item, trigger));
    this.mailService.popupRef?.close$
      .pipe(take(1))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.contextMenuHelper.ref?.destroy();
      });
  }

  private buildContextMenuItems(copyEmail: string): ContextMenuItem[] {
    const items: ContextMenuItem[] = [];
    items.push({
      id: 'copy_email',
      text: 'Copy Email Address',
      command: { type: 'copy-clipboard', value: copyEmail } as Commands.CopyClipboard,
      icon: { type: 'font-icon', value: actionToIcon['copy-clipboard'] },
    });
    return items;
  }

  async copyRecipientEmailAddress(event: MouseEvent, recipient: Recipient) {
    let openAt: { x: number; y: number } = event;
    const items = await this.buildContextMenuItems(recipient.mail);
    this.activeContextMenu = this.contextMenuService.open(
      openAt,
      {
        items,
        onInvoke: (item: ContextMenuItem) =>
          this.commandService.executeCommand(item.command, { item: this.model, searchId: this.searchId }),
      },
      { position: 'below' }
    );
    this.cdr.markForCheck();
    this.activeContextMenu.close$.pipe(untilDestroyed(this)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  private setViewRecipients(): void {
    this.countViewRecipients = this.viewModel?.recipients?.length;
    if (this.countViewRecipients < 3) return;
    this.countViewRecipients = 0;
    let countChars = this.defaultCountChars;
    this.viewModel?.recipients?.forEach((a) => {
      if (countChars <= 0) {
        return;
      }
      countChars -= (a.name ? a.name.length : a.mail.length) + 1;
      this.countViewRecipients++;
    });
    this.countViewRecipients < 2 ? (this.countViewRecipients = 2) : null;
  }

  setMethodsList() {
    this.methodsList = [];
    setTimeout(() => {
      if (this.titleRef?.nativeElement) this.methodsList.push({ id: 'title', fn: () => this.copySenderEmail() });
      if (this.countViewRecipients > 0) {
        this.methodsList = [
          ...this.methodsList,
          ...this.viewModel?.recipients?.slice(0, this.countViewRecipients)?.map((r, i) => {
            return { id: `recipient${i}`, fn: () => this.copyRecipientEmail(r, i) };
          }),
        ];
      }
      if (this.iconRef?.nativeElement) this.methodsList.push({ id: 'icon', fn: () => this.openRecipientsPopup() });
      if (this.openRef?.nativeElement) this.methodsList.push({ id: 'open', fn: () => this.open() });
      if (this.actionsRef?.nativeElement) this.methodsList.push({ id: 'actions', fn: () => this.actionsRef.nativeElement.click() });
      this.cdr.markForCheck();
    }, 0);
  }

  async open() {
    this.resultCommandService.executeCommand(this.model.view.title.onClick, { searchId: this.searchId }, 'result', {
      resources: this.eventResources,
    });
  }

  async openContextMenu(event: MouseEvent, source: ContextMenuInvokeSource) {
    const ref = await this.contextMenuHelper?.open(event, this.model, source);
    ref?.destroy$.pipe(untilDestroyed(this)).subscribe(() => this.cdr.markForCheck());
  }

  isAvailableSummary(action: Action) {
    return isEnabledSummary(action);
  }

  onSummary(event?: MouseEvent) {
    event.stopPropagation();
    this.eventService.event('preview.resource_action', {
      label: 'summary',
      location: { title: this.hubService.currentLocation },
      resources: this.eventResources,
    });
    this.commandService.executeCommand(
      {
        type: 'summary',
        command: this.model?.view?.title?.onClick,
        position: event,
        resourceId: this.model.id,
        resources: this.eventResources,
      } as Commands.Summary,
      { item: this.model, searchId: this.searchId }
    );
  }

  private async emitContextMenuCommand(item: ResultContextMenuItem, trigger: TelemetryTrigger): Promise<void> {
    let label: string = item.id;
    if (label === 'favorite') {
      this.model.isFavorite = !this.model.isFavorite;
      this.resultCommandService.toggleFavorite(this.model, this.eventResources, trigger, this.searchId);
      this.cdr.markForCheck();
      return;
    }
    await this.resultCommandService.executeCommand(item.command, { item: this.model, searchId: this.searchId }, 'result', {
      target: trigger,
      location: { title: this.hubService.currentLocation },
      label,
      resources: this.eventResources,
      ...this.telemetrySearch,
    });
    this.eventService.event('context_menu.open_from_preview', {
      label,
      location: { title: this.hubService.currentLocation },
      resources: this.eventResources,
      ...this.telemetrySearch,
    });
  }

  copySenderEmail() {
    this.copyClipboard(this.viewModel.senderEmail);
    const prevTooltip = this.viewModel.sender.tooltip;
    this.viewModel.sender.tooltip = 'Copied!';
    setTimeout(() => {
      this.tooltips.get(this.viewModel.isStarred ? 1 : 0).show();
      setTimeout(() => {
        this.tooltips.get(this.viewModel.isStarred ? 1 : 0).hide();
        this.viewModel.sender.tooltip = prevTooltip;
        this.cdr.markForCheck();
      }, 500);
    }, 0);
  }

  copyRecipientEmail(recipient: Recipient, index: number) {
    this.copyClipboard(recipient.mail);
    const prevTooltip = this.viewModel.recipients[index].tooltip;
    this.viewModel.recipients[index].tooltip = 'Copied!';
    setTimeout(() => {
      this.tooltips.get(this.viewModel.isStarred ? index + 2 : index + 1).show();
      setTimeout(() => {
        this.tooltips.get(this.viewModel.isStarred ? index + 2 : index + 1).hide();
        this.viewModel.recipients[index].tooltip = prevTooltip;
        this.cdr.markForCheck();
      }, 500);
    }, 0);
  }

  copyClipboard(value: string) {
    this.resultCommandService.executeCommand(
      {
        type: 'copy-clipboard',
        value: value,
      } as Commands.CopyClipboard,
      { searchId: this.telemetrySearch?.search?.searchId, item: this.model },
      'result'
    );
  }
  openRecipientsPopup() {
    this.mailService.openRecipientsPopup(this.viewModel?.recipients, this.iconRef);
  }

  unregisterKeyHandler() {
    if (!this.keyHandlerId) return;
    this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
    this.keyHandlerId = null;
    this.markedIndex = -1;
  }

  async handleKeys(keys: Array<KeyName>, event): Promise<void> {
    const modifiers = getModifiers(keys);
    const key = keys[0];
    if (key === 'tab') {
      event.stopPropagation();
      event.preventDefault();
      if (modifiers.length && modifiers[0] === 'shift') {
        if (this.markedIndex === 0) {
          this.componentFocusedExit.emit('prev');
        }
        this.markedIndex--;
      } else if (this.markedIndex === this.methodsList.length - 1) {
        this.componentFocusedExit.emit('next');
      } else {
        this.markedIndex++;
      }
    }
    if (isEnterKey(key)) {
      this.methodsList[this.markedIndex]?.fn();
      event.stopPropagation();
    }
    this.cdr.markForCheck();
  }
}
